import { useEffect, useState } from 'react';
import { ArrowRight, Check, Lock, RefreshCcw, ShieldCheck, X, CreditCard, User, Mail, Phone, MapPin, ChevronRight, Loader2 } from 'lucide-react';
import { Footer } from '../components/layout/Footer';

const pricingPlans = [
  {
    id: 'essential',
    name: 'Essential',
    regularPrice: '$5',
    promoPrice: 'Free',
    promoPriceValue: 0,
    regularPriceValue: 5,
    note: 'Single invitation',
    body: 'For one refined invitation with export-ready delivery.',
    features: ['1 invitation project', 'PNG + PDF export', 'Elegant style presets'],
    cta: 'Start Essential',
  },
  {
    id: 'signature',
    name: 'Signature',
    regularPrice: '$8',
    promoPrice: '$5',
    promoPriceValue: 5,
    regularPriceValue: 8,
    note: 'Most loved',
    body: 'For couples testing concepts, revisions, and multiple polished versions.',
    features: ['3 invitation concepts', 'Story + print exports', 'Priority save library'],
    cta: 'Choose Signature',
    featured: true,
  },
  {
    id: 'heirloom',
    name: 'Heirloom',
    regularPrice: '$15',
    promoPrice: '$10',
    promoPriceValue: 10,
    regularPriceValue: 15,
    note: 'Studio mode',
    body: 'For deeper experimentation, unlimited iterations, and premium delivery polish.',
    features: ['Unlimited generations', 'Full export suite', 'Best for planners and power users'],
    cta: 'Go Heirloom',
  },
];

const trustSignals = [
  { icon: Lock,        label: 'Secure checkout — SSL encrypted' },
  { icon: RefreshCcw,  label: 'Change your plan anytime'        },
  { icon: ShieldCheck, label: 'Your designs stay private and secure' },
];

const promoOfferEndsAt = new Date('2026-04-10T23:59:59+02:00');

function getPromoTimeLeft() {
  const now        = new Date();
  const difference = promoOfferEndsAt.getTime() - now.getTime();
  if (difference <= 0) return { days: '00', hours: '00', minutes: '00', seconds: '00' };
  return {
    days:    String(Math.floor(difference / 86400000)).padStart(2, '0'),
    hours:   String(Math.floor((difference / 3600000) % 24)).padStart(2, '0'),
    minutes: String(Math.floor((difference / 60000) % 60)).padStart(2, '0'),
    seconds: String(Math.floor((difference / 1000) % 60)).padStart(2, '0'),
  };
}

const STEPS = ['Plan', 'Details', 'Review', 'Payment'];

function CheckoutModal({ plan, onClose }) {
  const [step, setStep]             = useState(0);
  const [form, setForm]             = useState({ firstName: '', lastName: '', email: '', phone: '', address: '', city: '', country: 'South Africa' });
  const [errors, setErrors]         = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const isFree = plan.promoPriceValue === 0;

  function validate() {
    const e = {};
    if (!form.firstName.trim()) e.firstName = 'Required';
    if (!form.lastName.trim())  e.lastName  = 'Required';
    if (!form.email.trim() || !/^[^@]+@[^@]+\.[^@]+$/.test(form.email)) e.email = 'Valid email required';
    if (!form.phone.trim())   e.phone   = 'Required';
    if (!form.address.trim()) e.address = 'Required';
    if (!form.city.trim())    e.city    = 'Required';
    return e;
  }

  function handleChange(e) {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
    if (errors[name]) setErrors(er => { const n = { ...er }; delete n[name]; return n; });
  }

  function handleNext() {
    if (step === 1) {
      const errs = validate();
      if (Object.keys(errs).length) { setErrors(errs); return; }
    }
    setStep(s => s + 1);
  }

  function handleSubmit() {
    setSubmitting(true);
    setTimeout(() => { setSubmitting(false); setStep(4); }, 1500);
  }

  const displayPrice = isFree ? 'Free' : plan.promoPrice;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg rounded-[32px] bg-white shadow-2xl overflow-hidden">

        <button onClick={onClose} className="absolute right-5 top-5 z-10 rounded-full bg-[#F5F0EA] p-2 text-[#8A7A6A] hover:bg-[#EAE2D8] transition">
          <X size={18} />
        </button>

        {step < 4 && (
          <div className="flex items-center justify-between border-b border-[#EAE2D8] px-8 pt-8 pb-5">
            {STEPS.map((label, i) => (
              <div key={label} className="flex items-center gap-2">
                <div className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold transition ${
                  i < step  ? 'bg-[#C6A14B] text-white'
                  : i === step ? 'bg-[#1E1A17] text-white'
                  : 'bg-[#F0EAE2] text-[#9E907E]'
                }`}>
                  {i < step ? <Check size={13} /> : i + 1}
                </div>
                <span className={`hidden text-xs font-medium sm:block ${i === step ? 'text-[#211D1A]' : 'text-[#9E907E]'}`}>{label}</span>
                {i < STEPS.length - 1 && <ChevronRight size={14} className="text-[#D5C9BA] ml-1" />}
              </div>
            ))}
          </div>
        )}

        <div className="px-8 py-7 max-h-[78vh] overflow-y-auto">

          {step === 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#A07B39]">Selected Plan</p>
              <h2 className="mt-2 font-serif text-3xl text-[#211D1A]">{plan.name}</h2>
              <p className="mt-1 text-sm text-[#6C6258]">{plan.body}</p>
              <div className="mt-6 rounded-[24px] border border-[#E5D5BF] bg-[#FBF8F3] p-5">
                <div className="flex items-end gap-3">
                  <span className="font-serif text-4xl text-[#211D1A]">{displayPrice}</span>
                  {!isFree && <span className="pb-1 text-base text-[#9E907E] line-through">{plan.regularPrice}</span>}
                </div>
                <p className="mt-1 text-xs font-semibold uppercase tracking-[0.22em] text-[#A07B39]">First month only</p>
                <ul className="mt-4 space-y-2">
                  {plan.features.map(f => (
                    <li key={f} className="flex items-center gap-2 text-sm text-[#5E564F]">
                      <Check size={14} className="text-[#C6A14B] shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
              </div>
              {isFree && (
                <div className="mt-4 rounded-2xl border border-[#D5E8C0] bg-[#F4FAF0] px-4 py-3 text-sm text-[#4A7A2E]">
                  No payment required for the first month. Add billing details now so renewals are seamless.
                </div>
              )}
              <button onClick={handleNext} className="mt-6 w-full rounded-full bg-[#1E1A17] py-4 text-sm font-medium text-white hover:bg-[#0E0C0B] transition flex items-center justify-center gap-2">
                Continue <ArrowRight size={16} />
              </button>
            </div>
          )}

          {step === 1 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#A07B39]">Billing Details</p>
              <h2 className="mt-1 font-serif text-2xl text-[#211D1A]">Tell us about yourself</h2>
              <p className="mt-1 text-sm text-[#6C6258]">We'll use these details for your invoice and account.</p>
              <div className="mt-6 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <Field label="First name" name="firstName" icon={User}    value={form.firstName} error={errors.firstName} onChange={handleChange} placeholder="Jane" />
                  <Field label="Last name"  name="lastName"  icon={User}    value={form.lastName}  error={errors.lastName}  onChange={handleChange} placeholder="Doe" />
                </div>
                <Field label="Email address" name="email"   icon={Mail}    value={form.email}   error={errors.email}   onChange={handleChange} placeholder="jane@example.com" type="email" />
                <Field label="Phone number"  name="phone"   icon={Phone}   value={form.phone}   error={errors.phone}   onChange={handleChange} placeholder="+27 82 000 0000" type="tel" />
                <Field label="Street address" name="address" icon={MapPin} value={form.address} error={errors.address} onChange={handleChange} placeholder="123 Rose Street" />
                <div className="grid grid-cols-2 gap-3">
                  <Field label="City" name="city" icon={MapPin} value={form.city} error={errors.city} onChange={handleChange} placeholder="Cape Town" />
                  <div>
                    <label className="block text-xs font-medium text-[#6C6258] mb-1">Country</label>
                    <select name="country" value={form.country} onChange={handleChange}
                      className="w-full rounded-2xl border border-[#DDD3C8] bg-white px-3 py-3 text-sm text-[#211D1A] focus:border-[#C6A14B] focus:outline-none focus:ring-1 focus:ring-[#C6A14B]">
                      {['South Africa','United States','United Kingdom','Australia','Canada','Other'].map(c => <option key={c}>{c}</option>)}
                    </select>
                  </div>
                </div>
              </div>
              <div className="mt-6 flex gap-3">
                <button onClick={() => setStep(0)} className="flex-1 rounded-full border border-[#DDD3C8] py-4 text-sm font-medium text-[#6C6258] hover:border-[#C7B49A] transition">Back</button>
                <button onClick={handleNext}        className="flex-[2] rounded-full bg-[#1E1A17] py-4 text-sm font-medium text-white hover:bg-[#0E0C0B] transition flex items-center justify-center gap-2">
                  Review Order <ArrowRight size={16} />
                </button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#A07B39]">Order Review</p>
              <h2 className="mt-1 font-serif text-2xl text-[#211D1A]">Everything look right?</h2>
              <div className="mt-5 rounded-[24px] border border-[#E5D5BF] bg-[#FBF8F3] p-5 space-y-3">
                <Row label="Plan"  value={plan.name} />
                <Row label="Price" value={isFree ? 'Free (first month)' : `${plan.promoPrice}/month`} highlight />
                {!isFree && <Row label="After promo" value={`${plan.regularPrice}/month`} muted />}
                <div className="border-t border-[#E8DDD1] pt-3">
                  <Row label="Name"    value={`${form.firstName} ${form.lastName}`} />
                  <Row label="Email"   value={form.email} />
                  <Row label="Phone"   value={form.phone} />
                  <Row label="Address" value={`${form.address}, ${form.city}, ${form.country}`} />
                </div>
              </div>
              <div className="mt-4 rounded-2xl border border-[#E8DDD1] bg-[#FAFAF8] px-4 py-3 text-xs text-[#6C6258]">
                Payment via <span className="font-semibold text-[#211D1A]">Stitch Express</span> (verification in progress). You will be notified before any charge is made.
              </div>
              <div className="mt-6 flex gap-3">
                <button onClick={() => setStep(1)} className="flex-1 rounded-full border border-[#DDD3C8] py-4 text-sm font-medium text-[#6C6258] hover:border-[#C7B49A] transition">Back</button>
                <button onClick={handleNext}        className="flex-[2] rounded-full bg-[#1E1A17] py-4 text-sm font-medium text-white hover:bg-[#0E0C0B] transition flex items-center justify-center gap-2">
                  {isFree ? 'Confirm & Activate' : 'Proceed to Payment'} <ArrowRight size={16} />
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#A07B39]">{isFree ? 'Activation' : 'Payment'}</p>
              <h2 className="mt-1 font-serif text-2xl text-[#211D1A]">{isFree ? 'Activate your plan' : 'Complete payment'}</h2>
              {isFree ? (
                <div className="mt-5 rounded-[24px] border border-[#D5E8C0] bg-[#F4FAF0] p-5 text-sm text-[#4A7A2E]">
                  <p className="font-medium text-[#2E6018] text-base">Essential is free for the first month!</p>
                  <p className="mt-1">No payment needed right now. Click below to activate and start creating.</p>
                </div>
              ) : (
                <div className="mt-5 rounded-[24px] border border-[#E5D5BF] bg-[#FBF8F3] p-5">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="rounded-full bg-[#1E1A17] p-2"><CreditCard size={18} className="text-white" /></div>
                    <div>
                      <p className="text-sm font-semibold text-[#211D1A]">Stitch Express</p>
                      <p className="text-xs text-[#8A7A6A]">Verification in progress — coming very soon</p>
                    </div>
                  </div>
                  <div className="rounded-2xl border border-[#E8DDD1] bg-white px-4 py-4 text-sm text-[#6C6258]">
                    Once Stitch Express is verified, payment completes securely here. You will not be charged until fully confirmed.
                  </div>
                  <div className="mt-3 grid grid-cols-3 gap-2 text-center text-xs text-[#9E907E]">
                    {['SSL Encrypted','PCI Compliant','ZA Supported'].map(t => (
                      <span key={t} className="rounded-xl border border-[#E8DDD1] bg-white px-2 py-2">{t}</span>
                    ))}
                  </div>
                </div>
              )}
              <div className="mt-6 flex gap-3">
                <button onClick={() => setStep(2)} className="flex-1 rounded-full border border-[#DDD3C8] py-4 text-sm font-medium text-[#6C6258] hover:border-[#C7B49A] transition">Back</button>
                <button onClick={handleSubmit} disabled={submitting}
                  className="flex-[2] rounded-full bg-[#1E1A17] py-4 text-sm font-medium text-white hover:bg-[#0E0C0B] transition flex items-center justify-center gap-2 disabled:opacity-60">
                  {submitting ? <><Loader2 size={16} className="animate-spin" /> Processing…</> : isFree ? 'Activate for Free' : 'Confirm & Pay'}
                </button>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="py-6 text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-[#F4FAF0] border-2 border-[#A8D88A]">
                <Check size={30} className="text-[#4A7A2E]" />
              </div>
              <h2 className="font-serif text-3xl text-[#211D1A]">{isFree ? "You're all set!" : 'Order received!'}</h2>
              <p className="mt-3 text-sm text-[#6C6258] leading-7">
                {isFree
                  ? 'Your Essential plan is active. Start crafting your first invitation now.'
                  : `Thanks ${form.firstName}! We've saved your order for the ${plan.name} plan. You'll hear from us once Stitch Express is live to complete payment.`}
              </p>
              <button onClick={onClose}
                className="mt-7 inline-flex items-center gap-2 rounded-full bg-[#1E1A17] px-8 py-4 text-sm font-medium text-white hover:bg-[#0E0C0B] transition">
                {isFree ? 'Start Creating' : 'Back to Pricing'} <ArrowRight size={16} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, name, icon: Icon, value, error, onChange, placeholder, type = 'text' }) {
  return (
    <div>
      <label className="block text-xs font-medium text-[#6C6258] mb-1">{label}</label>
      <div className="relative">
        {Icon && <Icon size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#B0A090] pointer-events-none" />}
        <input
          type={type} name={name} value={value} onChange={onChange} placeholder={placeholder}
          className={`w-full rounded-2xl border ${error ? 'border-red-400' : 'border-[#DDD3C8]'} bg-white ${Icon ? 'pl-9' : 'pl-3'} pr-3 py-3 text-sm text-[#211D1A] placeholder-[#B8AFA8] focus:border-[#C6A14B] focus:outline-none focus:ring-1 focus:ring-[#C6A14B] transition`}
        />
      </div>
      {error && <p className="mt-0.5 text-xs text-red-500">{error}</p>}
    </div>
  );
}

function Row({ label, value, highlight, muted }) {
  return (
    <div className="flex justify-between text-sm py-0.5">
      <span className="text-[#8A7A6A]">{label}</span>
      <span className={`font-medium ${highlight ? 'text-[#A07B39]' : muted ? 'text-[#B0A090] line-through' : 'text-[#211D1A]'}`}>{value}</span>
    </div>
  );
}

export default function PricingPage() {
  const [promoTimeLeft, setPromoTimeLeft] = useState(getPromoTimeLeft);
  const [selectedPlan, setSelectedPlan]   = useState(null);

  useEffect(() => {
    const interval = window.setInterval(() => setPromoTimeLeft(getPromoTimeLeft()), 1000);
    return () => window.clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-[#FBF8F3] text-[#211D1A]">

      {selectedPlan && (
        <CheckoutModal plan={selectedPlan} onClose={() => setSelectedPlan(null)} />
      )}

      <section className="border-b border-[#E8E0D6] bg-white pb-24 pt-32 md:pt-36">
        <div className="mx-auto max-w-7xl px-6">

          <div className="mx-auto max-w-3xl text-center">
            <p className="mb-4 text-xs font-semibold uppercase tracking-[0.35em] text-[#8A7A6A]">Pricing</p>
            <h1 className="font-serif text-4xl leading-tight text-[#211D1A] md:text-6xl">
              Soft promotional pricing for the next month.
            </h1>
            <p className="mt-5 text-base leading-8 text-[#6C6258] md:text-lg">
              Regular pricing shown transparently — first-month launch offer live until April 10, 2026.
            </p>
          </div>

          <div className="mx-auto mt-10 max-w-3xl rounded-[32px] border border-[#E5D5BF] bg-[linear-gradient(180deg,#FFF9F1_0%,#F8EEDF_100%)] p-6 text-center shadow-soft">
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#A07B39]">Launch offer ends April 10, 2026</p>
            <div className="mt-4 flex items-center justify-center gap-3">
              {[
                { label: 'Days',    value: promoTimeLeft.days    },
                { label: 'Hours',   value: promoTimeLeft.hours   },
                { label: 'Minutes', value: promoTimeLeft.minutes },
                { label: 'Seconds', value: promoTimeLeft.seconds },
              ].map((item) => (
                <div key={item.label} className="min-w-[68px] rounded-[24px] border border-[#E1D0BA] bg-white/85 px-3 py-4">
                  <div className="font-serif text-3xl text-[#211D1A]">{item.value}</div>
                  <div className="mt-1 text-[10px] font-medium uppercase tracking-[0.22em] text-[#9E907E]">{item.label}</div>
                </div>
              ))}
            </div>
            <p className="mt-4 text-sm text-[#6C6258]">First month only: Essential free, Signature $5, Heirloom $10.</p>
          </div>

          <div className="mt-14 grid gap-6 lg:grid-cols-3">
            {pricingPlans.map((plan) => (
              <div
                key={plan.name}
                className={`rounded-[34px] border p-8 shadow-soft transition hover:-translate-y-1 hover:shadow-md ${
                  plan.featured ? 'border-[#CAB18A] bg-[#F8F1E5]' : 'border-[#E8DDD1] bg-[#FBF8F3]'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium uppercase tracking-[0.3em] text-[#8A7A6A]">{plan.note}</p>
                    <h3 className="mt-3 font-serif text-3xl text-[#211D1A]">{plan.name}</h3>
                  </div>
                  {plan.featured && (
                    <span className="rounded-full bg-[#1E1A17] px-3 py-1 text-xs font-medium text-white">Most chosen</span>
                  )}
                </div>
                <div className="mt-8 flex items-end gap-3">
                  <p className="font-serif text-5xl text-[#211D1A]">{plan.promoPrice}</p>
                  <p className="pb-2 text-lg text-[#9E907E] line-through">{plan.regularPrice}</p>
                </div>
                <p className="mt-2 text-xs font-semibold uppercase tracking-[0.22em] text-[#A07B39]">First month only</p>
                <p className="mt-3 text-sm leading-7 text-[#6C6258]">{plan.body}</p>
                <ul className="mt-8 space-y-3">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-3 text-sm text-[#5E564F]">
                      <Check size={16} className="mt-0.5 shrink-0 text-[#C6A14B]" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => setSelectedPlan(plan)}
                  className={`mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-4 text-sm font-medium transition hover:scale-[1.02] active:scale-100 ${
                    plan.featured
                      ? 'bg-[#1E1A17] text-white hover:bg-[#0E0C0B]'
                      : 'border border-[#DCCFBE] bg-white text-[#211D1A] hover:border-[#C7B49A] hover:bg-white'
                  }`}
                >
                  {plan.cta} <ArrowRight size={16} />
                </button>
              </div>
            ))}
          </div>

          <div className="mx-auto mt-12 flex flex-wrap justify-center gap-x-8 gap-y-3">
            {trustSignals.map(({ icon: Icon, label }) => (
              <span key={label} className="flex items-center gap-2 text-sm text-[#8A7A6A]">
                <Icon size={15} className="text-[#C6A14B]" />
                {label}
              </span>
            ))}
          </div>

          <div className="mx-auto mt-14 max-w-2xl rounded-[32px] border border-[#E8DDD1] bg-[#FBF8F3] p-8">
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#8A7A6A]">Good to know</p>
            <ul className="mt-6 space-y-5">
              <li className="flex items-start gap-3 text-sm leading-7 text-[#5E564F]">
                <Check size={16} className="mt-1 shrink-0 text-[#C6A14B]" />
                <span><span className="font-medium text-[#211D1A]">Essential is free for the first month.</span> After that it becomes $5/month. No surprises.</span>
              </li>
              <li className="flex items-start gap-3 text-sm leading-7 text-[#5E564F]">
                <Check size={16} className="mt-1 shrink-0 text-[#C6A14B]" />
                <span><span className="font-medium text-[#211D1A]">Your invitations are always saved.</span> Even if you downgrade, your library stays accessible.</span>
              </li>
              <li className="flex items-start gap-3 text-sm leading-7 text-[#5E564F]">
                <Check size={16} className="mt-1 shrink-0 text-[#C6A14B]" />
                <span><span className="font-medium text-[#211D1A]">Stitch Express payment — verification in progress.</span> We'll notify you before any charge is processed.</span>
              </li>
            </ul>
          </div>

        </div>
      </section>
      <Footer />
    </div>
  );
}
